/**
 * File: qaqcPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v3.1.2
 * Created: 2025-12-04
 * Last Updated: 2026-02-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Icons
import { FaBarsProgress } from "react-icons/fa6";
import { FaClock } from "react-icons/fa6";
import { FaFileContract } from "react-icons/fa6";
import { FaMoneyBill } from "react-icons/fa6";
import { IoMdCheckmark } from "react-icons/io";

// QAQC Page
export const qaqcPage = {
    meta: {
        id: "qaqc-page",
    },
    hero: {
        image: "/svgs/qaqc_hero_system-isometric.svg",
        headline: "Professional QA/QC Engineering Reviews",
        subheadline:
            "Work with licensed engineers to improve accuracy, safety, and constructability before you build.",
        primaryButton: {
            text: "Get Started",
            type: "primary",
        },
        secondaryButton: {
            text: "Get Estimate",
            type: "secondary",
        },
    },
    planCard: {
        title: "QA/QC Review Packages",
        cardType: "package",
        plans: [
            {
                id: "plan-qaqc-basic",
                header: "Basic",
                description:
                    "Quick schematic review with core checklists and actionable markups.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-qaqc-standard",
                header: "Standard",
                description:
                    "In-depth design review with coordination, calculations, and feedback.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-qaqc-premium",
                header: "Premium",
                description:
                    "Full construction document review including compliance and follow-ups.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
        ],
        features: [
            {
                id: "feature-qaqc-1",
                icon: <FaMoneyBill />,
                text: "Flexible Pricing",
            },
            {
                id: "feature-qaqc-2",
                icon: <FaFileContract />,
                text: "No Long-Term Contracts",
            },
            {
                id: "feature-qaqc-3",
                icon: <FaBarsProgress />,
                text: "Regular Project Updates",
            },
            {
                id: "feature-qaqc-4",
                icon: <FaClock />,
                text: "Start Within 24-48 Hours",
            },
        ],
    },
    articles: [
        {
            id: "article-qaqc-1",
            reverse: false,
            image: "/svgs/qaqc_checklists_floor-plan-checklist.svg",
            headline: "Comprehensive Review Checklists",
            description:
                "Receive clear, structured review reports that outline design issues, risks, and improvement opportunities across every discipline.",
        },
        {
            id: "article-qaqc-2",
            reverse: true,
            image: "../svgs/qaqc_markups_clouded-mechanical-plan.svg",
            headline: "Actionable Comments & Markups",
            description:
                "Your plans come back with precise engineering comments, redlines, and markups giving your team the exact guidance needed to refine design quality.",
        },
        {
            id: "article-qaqc-3",
            reverse: false,
            image: "/svgs/qaqc_construction_plumbing-isometric.svg",
            headline: "Catch Issues Before Construction",
            description:
                "Identify design conflicts early, reduce costly field revisions, and avoid delays by resolving problems long before they reach the job site.",
        },
    ],
    comparisonTable: {
        transpose: true,
        transposeHeader: ["Features", "Basic", "Standard", "Premium"],
        object: [
            {
                "Stage / Tier": "Schematic Design (SD)",
                "Engineering Discipline": "Mechanical or Plumbing",

                "Constructability Review": <IoMdCheckmark />,
                "Design Document Review": "—",
                "Engineering Calculations Review": "—",
                "System Layout Review": "—",
                "Coordination / Clash Review": "Limited",
                "Detail Review": "—",
                "Energy / Compliance Document Review": "—",

                "Sheets Reviewed": "Up to 5",
                "Revision / Follow-Up Cycles Included": "1",
                "Actionable Markups": <IoMdCheckmark />,
                "Comprehensive Checklist": <IoMdCheckmark />,
                "Implementation of Prior Markups": "—",
                Exclusions: "Design changes, load calculations, drafting",
            },
            {
                "Stage / Tier": "Design Development (DD)",
                "Engineering Discipline": "Mechanical or Plumbing",

                "Constructability Review": <IoMdCheckmark />,
                "Design Document Review": <IoMdCheckmark />,
                "Engineering Calculations Review": <IoMdCheckmark />,
                "System Layout Review": <IoMdCheckmark />,
                "Coordination / Clash Review": <IoMdCheckmark />,
                "Detail Review": "—",
                "Energy / Compliance Document Review": "—",

                "Sheets Reviewed": "Up to 12",
                "Revision / Follow-Up Cycles Included": "2",
                "Actionable Markups": <IoMdCheckmark />,
                "Comprehensive Checklist": <IoMdCheckmark />,
                "Implementation of Prior Markups": <IoMdCheckmark />,
                Exclusions: "Same",
            },
            {
                "Stage / Tier": "Construction Documents (CD)",
                "Engineering Discipline": "Mechanical or Plumbing",

                "Constructability Review": <IoMdCheckmark />,
                "Design Document Review": <IoMdCheckmark />,
                "Engineering Calculations Review": <IoMdCheckmark />,
                "System Layout Review": <IoMdCheckmark />,
                "Coordination / Clash Review": <IoMdCheckmark />,
                "Detail Review": <IoMdCheckmark />,
                "Energy / Compliance Document Review": <IoMdCheckmark />,

                "Sheets Reviewed": "Up to 25+",
                "Revision / Follow-Up Cycles Included": "2",
                "Actionable Markups": <IoMdCheckmark />,
                "Comprehensive Checklist": <IoMdCheckmark />,
                "Implementation of Prior Markups": <IoMdCheckmark />,
                Exclusions: "Same",
            },
        ],
    },
    faq: [
        {
            id: "faq-qaqc-1",
            header: "What does the QA/QC review process look like?",
            content:
                "After you submit a QA/QC request, our team assigns the review to a licensed engineer who specializes in the project's discipline. The engineer performs a structured review using our standardized checklists, examines drawings for accuracy and constructability, and prepares detailed comments and markups.",
        },
        {
            id: "faq-qaqc-2",
            header: "How far in advance should we schedule QA/QC work?",
            content:
                "We recommend scheduling reviews at least one week before major project deadlines. Most QA/QC reviews are completed within one to three business days after approval.",
        },
        {
            id: "faq-qaqc-3",
            header: "What deliverables are included in a QA/QC request?",
            content:
                "You will receive a comprehensive engineering review package, including detailed review checklists, plan markups, redlines highlighting issues, and any high-priority flags requiring immediate attention.",
        },
        {
            id: "faq-qaqc-4",
            header: "What types of QA/QC reviews do you offer?",
            content:
                "QA/QC reviews can be performed at any stage of the project. Common types include: Schematic Design, Design Development, Construction Document, Code Compliance, Discipline Coordination, and Follow-Up Reviews.",
        },
        {
            id: "faq-qaqc-5",
            header: "Who conducts the QA/QC reviews?",
            content:
                "All reviews are performed by licensed engineers or highly experienced professionals trained to identify constructability issues, design conflicts, coordination gaps, and code compliance concerns.",
        },
        {
            id: "faq-qaqc-6",
            header: "Are revisions or follow-up reviews included?",
            content:
                "Initial QA/QC reviews include all comments and markups based on the submitted set. Follow-up reviews after updates are available and billed separately based on scope.",
        },
    ],
    cta: {
        label: "Request a QA/QC Review",
        headline: "Improve Your Project Today",
        description:
            "Submit your plans to our licensed engineering team and receive a detailed review that enhances quality, code compliance, and constructability.",
    },
};
